import { useState } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle2, Phone, Mail, User as UserIcon } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { formatApiError } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadCapture({ recommendationId, onSuccess }) {
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    consent_marketing: false, consent_transfer: false,
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_marketing || !form.consent_transfer) {
      toast.error("Devi accettare entrambi i consensi per essere ricontattato");
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/leads`,
        { ...form, recommendation_id: recommendationId },
        { withCredentials: true }
      );
      if (onSuccess) { onSuccess(); return; }
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.title) {
        // Structured backend hint (invalid/fake email or phone)
        const bullet = (detail.suggestions || []).slice(0, 3).map((s) => `• ${s}`).join("\n");
        toast.error(`${detail.title}\n${detail.message}${bullet ? "\n\n" + bullet : ""}`, { duration: 9000 });
      } else {
        toast.error(formatApiError(detail) || "Errore invio");
      }
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-12 rounded-3xl border border-[#EAEAEA] bg-white p-8 text-center"
        data-testid="lead-success"
      >
        <CheckCircle2 className="w-10 h-10 text-[#0055FF] mx-auto" />
        <h3 className="mt-4 font-heading text-xl font-medium">Richiesta ricevuta</h3>
        <p className="mt-2 text-sm text-[#666]">Un consulente ti contatterà con offerte dedicate entro 24-48 ore.</p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="mt-12 rounded-3xl border border-[#EAEAEA] bg-white p-6 md:p-8"
      data-testid="lead-capture-card"
    >
      <div className="max-w-2xl mx-auto">
        <span className="text-xs uppercase tracking-widest text-[#0055FF] font-medium">Un ultimo passo</span>
        <h3 className="mt-2 font-heading text-2xl font-semibold tracking-tight">Lascia i tuoi contatti per vedere i risultati</h3>
        <p className="mt-2 text-sm text-[#666]">
          Compila i campi qui sotto per sbloccare la selezione di auto pensata per te. I nostri consulenti potranno anche contattarti con offerte reali dai concessionari partner della tua zona.
        </p>

        <form onSubmit={submit} className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="lead-form">
          <div className="relative sm:col-span-2">
            <UserIcon className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#666]" />
            <Input required placeholder="Nome e cognome" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-12 pl-11 rounded-2xl border-[#EAEAEA] bg-white" data-testid="lead-name" />
          </div>
          <div className="relative">
            <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#666]" />
            <Input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-12 pl-11 rounded-2xl border-[#EAEAEA] bg-white" data-testid="lead-email" />
          </div>
          <div className="relative">
            <Phone className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#666]" />
            <Input required placeholder="Telefono" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-12 pl-11 rounded-2xl border-[#EAEAEA] bg-white" data-testid="lead-phone" />
          </div>

          <label className="sm:col-span-2 flex items-start gap-3 text-sm text-[#333] mt-2">
            <input type="checkbox" checked={form.consent_marketing} onChange={(e) => setForm({ ...form, consent_marketing: e.target.checked })} className="mt-1 w-4 h-4 accent-[#0055FF]" data-testid="lead-consent-marketing" required />
            <span>
              <b>Consenso marketing</b> — acconsento a essere contattato via email, telefono o SMS per offerte relative all'acquisto di un'automobile.
            </span>
          </label>
          <label className="sm:col-span-2 flex items-start gap-3 text-sm text-[#333]">
            <input type="checkbox" checked={form.consent_transfer} onChange={(e) => setForm({ ...form, consent_transfer: e.target.checked })} className="mt-1 w-4 h-4 accent-[#0055FF]" data-testid="lead-consent-transfer" required />
            <span>
              <b>Consenso alla cessione a terzi</b> (concessionari partner) dei miei dati per finalità commerciali, ai sensi del Reg. UE 2016/679 (GDPR).
            </span>
          </label>

          <button
            type="submit"
            disabled={busy}
            className="sm:col-span-2 h-12 rounded-full bg-[#111] text-white hover:bg-[#333] transition inline-flex items-center justify-center gap-2 disabled:opacity-60 mt-2"
            data-testid="lead-submit"
          >
            <Send className="w-4 h-4" />
            {busy ? "Invio in corso…" : "Invia richiesta"}
          </button>
          <p className="sm:col-span-2 text-xs text-[#666] text-center">
            I tuoi dati sono trattati secondo l'informativa privacy. Puoi revocare i consensi in qualsiasi momento.
          </p>
        </form>
      </div>
    </motion.div>
  );
}
